import {
  defineComponent,
  h,
  ref,
  computed,
  onMounted,
  onUpdated,
  onBeforeUnmount,
  withModifiers,
} from "vue";
import { useDisplay } from "vuetify";
import { VBtn, VCheckboxBtn } from "vuetify/components";

/**
 * v-data-table / v-data-table-server için "yatay kaydırmasız" sarmalayıcı.
 *
 * - Tablo genişliğe sığmıyorsa en sağdaki (öncelik sırası düşük) sütunlar
 *   tablodan çıkarılır ve satırın altında açılan "Detay" panelinde gösterilir.
 * - Telefonda (<600px) kart görünümünde yalnızca ilk 4 alan + işlem butonları
 *   görünür; kalanlar "Detay" ile açılır (dikeyde yer kaplamasın diye).
 * - Öncelik = header sırası. İlk sütun ve işlem sütunları (process/actions...)
 *   her zaman görünür kalır.
 * - Sayfa kendi show-expand / expanded-row'unu kullanıyorsa dokunulmaz.
 */
const PINNED = /^(process|processes|actions?|islemler|operations?)$/i;
const MOBILE_BREAKPOINT = 600;
const STATUS = /(status|durum|state)/i;
const MOBILE_PRIMARY = 4; // telefonda görünen ana alan sayısı (ilk sütun dahil)
const DESKTOP_PRIMARY = 8; // tablet/masaüstünde okunabilirlik için en fazla ana sütun (ilk sütun dahil)

/**
 * Ana veri / detay ayrımı: her sütuna anlamına göre bir önem puanı verilir
 * (düşük = ana veri). Detaya en yüksek puanlılar (id, not, telefon, IMEI...) gider.
 * Sayfa isterse header'a `priority: 0.5` gibi sayısal değer verip bir sütunu ana veri
 * yapabilir (<= 1 olanlar ek ana alan sayılır, ekranda hep görünür kalır).
 *   0 ilk sütun (kimlik)      1 durum/statü        2 ad, plaka, kullanıcı, bayi...
 *   3 tarih/saat              4 diğer              6 teknik/ikincil (id, not, adres...)
 */
const RX_STATUS = /(status|durum|state|aktif|active)/i;
const RX_MAIN = /(name|adi|adı|isim|title|baslik|başlık|plate|plaka|user|kullanici|kullanıcı|customer|musteri|müşteri|dealer|bayi|model|vehicle|arac|araç|driver|surucu|sürücü|owner|sahib)/i;
const RX_DATE = /(date|tarih|created|updated|olusturul|oluşturul|guncel|güncel|(start|finish|end)time|baslangic|başlangıç|bitis|bitiş)/i;
const RX_LOW = /((^|[._\s])(id|guid|uuid)$)|(_id$)|([a-z]Id$)|(^|[._\s])(note|notes|not|comment|yorum|description|aciklama|açıklama|address|adres|mail|email|e-posta|phone|telefon|imei|iccid|token|url|password|sifre|şifre|extra|limit|hold|thold)($|[._\s])/i;

function importance(hd, index) {
  const text = `${hd.key ?? ""} ${hd.title ?? ""}`;
  if (index === 0) return 0;
  if (typeof hd.priority === "number") return hd.priority; // sayfanın açık tercihi
  if (RX_LOW.test(text)) return 6;
  if (RX_STATUS.test(text)) return 1;
  if (RX_MAIN.test(text)) return 2;
  if (RX_DATE.test(text)) return 3;
  return 4;
}

function getValue(item, header) {
  if (typeof header.value === "function") return header.value(item);
  const path = typeof header.value === "string" ? header.value : header.key;
  return String(path)
    .split(".")
    .reduce((o, k) => (o === null || o === undefined ? o : o[k]), item);
}

function display(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Evet" : "Hayır";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function createResponsiveDataTable(Base, name) {
  return defineComponent({
    name,
    inheritAttrs: false,
    setup(_, { attrs, slots }) {
      const root = ref(null);
      const hiddenCount = ref(0);
      const stillOverflow = ref(false);
      const { width } = useDisplay();
      const isMobile = computed(() => width.value < MOBILE_BREAKPOINT);
      let lastWidth = 0;
      let lastSignature = "";
      let observer = null;
      let raf = 0;

      const wrapper = () => root.value?.querySelector(".v-table__wrapper");

      function check() {
        if (isMobile.value) return;
        const w = wrapper();
        if (!w) return;
        const overflow = w.scrollWidth > w.clientWidth + 1;
        const droppable = Number(root.value.dataset.droppable || 0);
        if (overflow && hiddenCount.value < droppable) {
          hiddenCount.value++;
          stillOverflow.value = false;
        } else {
          stillOverflow.value = overflow;
        }
      }

      // Sayfaların <style scoped> kuralları (.my-table :deep(td) gibi) tablonun kök elementinde
      // parent'ın data-v-xxx özniteliğini bekler. Sarmalayıcı araya girdiği için bu öznitelik
      // yalnızca sarmalayıcı div'inde kalıyor; iç tablo köküne de kopyalıyoruz.
      function copyScopeIds() {
        const el = root.value;
        const inner = el && el.firstElementChild;
        if (!inner) return;
        el.getAttributeNames().forEach((name) => {
          if (name.startsWith("data-v-") && !inner.hasAttribute(name)) {
            inner.setAttribute(name, "");
          }
        });
      }

      onMounted(() => {
        copyScopeIds();
        lastWidth = root.value.clientWidth;
        // Yerleşimi ResizeObserver içinde hemen değiştirmek "ResizeObserver loop completed
        // with undelivered notifications" hatası verir; işi bir sonraki kareye ertele.
        observer = new ResizeObserver(() => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            if (!root.value) return;
            const current = root.value.clientWidth || 0;
            if (current > lastWidth + 8) hiddenCount.value = 0; // genişlediyse yeniden dene
            lastWidth = current;
            check();
          });
        });
        observer.observe(root.value);
        check();
      });
      onUpdated(() => {
        copyScopeIds();
        check();
      });
      onBeforeUnmount(() => {
        cancelAnimationFrame(raf);
        if (observer) observer.disconnect();
      });

      return () => {
        const headers = Array.isArray(attrs.headers)
          ? attrs.headers.filter((hd) => hd && typeof hd === "object")
          : null;
        const userExpand =
          attrs.showExpand !== undefined ||
          attrs["show-expand"] !== undefined ||
          slots["expanded-row"];

        // Uygun değilse (headers yok / sayfa kendi expand'ini kullanıyor) aynen geçir
        if (!headers || userExpand) {
          return h("div", { ref: root, class: "rdt-root", "data-droppable": 0 }, [
            h(Base, attrs, slots),
          ]);
        }

        const signature = headers.map((hd) => hd.key).join("|");
        if (signature !== lastSignature) {
          lastSignature = signature;
          hiddenCount.value = 0;
        }

        const droppable = headers
          .map((hd, i) => ({ hd, i }))
          .filter(
            ({ hd, i }) =>
              i > 0 &&
              !PINNED.test(String(hd.key ?? "")) &&
              !String(hd.key ?? "").startsWith("data-table-"),
          )
          .map(({ i }) => i);

        // Önem sırası: en önemli başta, aynı puanda özgün sıra korunur
        const byImportance = [...droppable].sort(
          (a, b) => importance(headers[a], a) - importance(headers[b], b) || a - b,
        );
        // Sayfanın "ana" işaretlediği (priority <= 1) sütunlar limitin dışında ek olarak gösterilir
        const explicitMain = droppable.filter(
          (i) => typeof headers[i].priority === "number" && headers[i].priority <= 1,
        ).length;
        const primaryCap = (isMobile.value ? MOBILE_PRIMARY : DESKTOP_PRIMARY) + explicitMain;
        let hideCount = Math.max(hiddenCount.value, droppable.length - (primaryCap - 1));
        hideCount = Math.max(0, Math.min(hideCount, droppable.length));
        // en önemsiz hideCount sütun detaya gider
        const hiddenIdx = new Set(byImportance.slice(byImportance.length - hideCount));
        const visible = headers.filter((_, i) => !hiddenIdx.has(i));
        const hidden = headers.filter((_, i) => hiddenIdx.has(i));

        const forwarded = {};
        Object.keys(slots).forEach((n) => {
          forwarded[n] = (d) => slots[n](d);
        });

        const props = { ...attrs, headers: visible };

        // Satır anahtarı: birçok sayfada item-value yok ya da satırlarda olmayan bir alana
        // (örn. "name") işaret ediyor; bu durumda tüm satırlar aynı anahtarı paylaşıp
        // "Detay"a basınca hepsi birden açılıyordu. Seçim (show-select) yoksa item-value
        // yalnızca genişletme için kullanıldığından satırı nesne kimliğiyle ayırt ediyoruz.
        const noSelect = attrs.showSelect === undefined && attrs["show-select"] === undefined;
        if (noSelect) props.itemValue = (item) => item;

        const cellOf = (hd, p) => {
          const slot = slots[`item.${hd.key}`];
          const value = getValue(p.item, hd);
          return slot ? slot({ ...p, value, column: hd }) : display(value);
        };

        if (isMobile.value) {
          // Telefon: her satır bir kart (başlık + rozet + özet + "Detayları Göster")
          const isPinned = (hd) => PINNED.test(String(hd.key ?? ""));
          const title = visible.find((hd) => !isPinned(hd));
          const badge = visible.find(
            (hd) => hd !== title && !isPinned(hd) && STATUS.test(String(hd.key ?? "")),
          );
          const meta = visible.filter((hd) => hd !== title && hd !== badge && !isPinned(hd));
          const actions = visible.filter(isPinned);

          if (attrs.onUpdateSortBy === undefined && attrs["onUpdate:sortBy"] === undefined) {
            props.hideDefaultHeader = true;
          }
          const showSelect = attrs.showSelect !== undefined || attrs["show-select"] !== undefined;
          forwarded.body = (sp) => {
            const rows = sp.internalItems || [];
            if (!rows.length) {
              const loading = attrs.loading === "" || attrs.loading === true;
              const text = loading
                ? (attrs.loadingText ?? attrs["loading-text"] ?? "Yükleniyor...")
                : (attrs.noDataText ?? attrs["no-data-text"] ?? "Kayıt bulunamadı");
              return h("tr", [
                h("td", { class: "rdt-cards-cell" }, [
                  slots["no-data"] && !loading
                    ? slots["no-data"]()
                    : h("div", { class: "rdt-cards-empty" }, text),
                ]),
              ]);
            }
            return h("tr", { class: "rdt-cards-row" }, [
              h("td", { class: "rdt-cards-cell" }, [
                h(
                  "div",
                  { class: "rdt-cards" },
                  rows.map((internalItem, index) => {
                    const item = internalItem.raw ?? internalItem;
                    const p = { item, internalItem, index };
                    const open = sp.isExpanded ? sp.isExpanded(internalItem) : false;
                    return h("div", { class: "rdt-card", key: internalItem.key ?? index }, [
                      h("div", { class: "rdt-card-head" }, [
                        showSelect && sp.toggleSelect
                          ? h(VCheckboxBtn, {
                              class: "rdt-card-select",
                              modelValue: sp.isSelected([internalItem]),
                              "onUpdate:modelValue": () => sp.toggleSelect(internalItem),
                            })
                          : null,
                        h("div", { class: "rdt-card-title" }, title ? cellOf(title, p) : null),
                        badge ? h("div", { class: "rdt-card-badge" }, cellOf(badge, p)) : null,
                      ]),
                      meta.length
                        ? h(
                            "div",
                            { class: "rdt-card-meta" },
                            meta.map((hd) =>
                              h("div", { class: "rdt-meta", key: hd.key }, [
                                h("div", { class: "rdt-meta-label" }, hd.title ?? hd.key),
                                h("div", { class: "rdt-meta-value" }, cellOf(hd, p)),
                              ]),
                            ),
                          )
                        : null,
                      actions.length
                        ? h(
                            "div",
                            { class: "rdt-card-actions" },
                            actions.map((hd) => h("div", { key: hd.key }, cellOf(hd, p))),
                          )
                        : null,
                      hidden.length
                        ? h(
                            "button",
                            {
                              type: "button",
                              class: ["rdt-card-toggle", { "is-open": open }],
                              onClick: () => sp.toggleExpand && sp.toggleExpand(internalItem),
                            },
                            [
                              h("span", open ? "Detayları Gizle" : "Detayları Göster"),
                              h("i", { class: "mdi mdi-chevron-down rdt-card-chevron" }),
                            ],
                          )
                        : null,
                      hidden.length && open
                        ? h(
                            "div",
                            { class: "rdt-card-details" },
                            hidden.map((hd) =>
                              h("div", { class: "rdt-detail", key: hd.key }, [
                                h("div", { class: "rdt-detail-label" }, hd.title ?? hd.key),
                                h("div", { class: "rdt-detail-value" }, cellOf(hd, p)),
                              ]),
                            ),
                          )
                        : null,
                    ]);
                  }),
                ),
              ]),
            ]);
          };
        } else if (hidden.length) {
          props.showExpand = true;
          props.expandOnClick = false;

          if (!slots["item.data-table-expand"]) {
            forwarded["item.data-table-expand"] = (p) =>
              h(
                VBtn,
                {
                  size: "small",
                  variant: "tonal",
                  color: "primary",
                  rounded: "lg",
                  class: "rdt-expand",
                  title: p.isExpanded ? "Detayları gizle" : "Detayları göster",
                  appendIcon: p.isExpanded ? "$collapse" : "$expand",
                  onClick: withModifiers(() => p.toggleExpand(p.internalItem), ["stop"]),
                },
                () => "Detay",
              );
          }

          forwarded["expanded-row"] = (p) =>
            h("tr", { class: "rdt-details-row" }, [
              h("td", { colspan: p.columns.length, class: "rdt-details-cell" }, [
                h("div", { class: "rdt-panel" }, [
                  h(
                    "button",
                    {
                      type: "button",
                      class: "rdt-card-toggle is-open",
                      onClick: () => p.toggleExpand(p.internalItem),
                    },
                    [
                      h("span", "Detayları Gizle"),
                      h("i", { class: "mdi mdi-chevron-down rdt-card-chevron" }),
                    ],
                  ),
                  h(
                    "div",
                    { class: "rdt-card-details rdt-card-details--wide" },
                    hidden.map((hd) =>
                      h("div", { class: "rdt-detail", key: hd.key }, [
                        h("div", { class: "rdt-detail-label" }, hd.title ?? hd.key),
                        h("div", { class: "rdt-detail-value" }, cellOf(hd, p)),
                      ]),
                    ),
                  ),
                ]),
              ]),
            ]);
        }

        return h(
          "div",
          {
            ref: root,
            class: ["rdt-root", { "rdt-root--scroll": stillOverflow.value }],
            "data-droppable": droppable.length,
          },
          [h(Base, props, forwarded)],
        );
      };
    },
  });
}
